import { useCallback, useState } from 'react';

/**
 * Phase 5.G PR-6 — per-user, per-site favourites for the gates list.
 *
 * Customers asked for a way to lift the gates they use most to the top
 * of the list — e.g. the main entrance gate on a factory with 50+
 * doors. We persist the chosen IDs in localStorage keyed by user +
 * site, so each operator sees their own order on each site.
 *
 * No backend change needed for v1 — pins live only on the device. A
 * future PR can sync this to the server if customers ask for shared
 * pins across phones, but device-local is the right default (each
 * operator has their own thumb-reach).
 *
 * Order semantics: most recently pinned floats to the top. So if you
 * pin "כניסה" then later pin "ייצור", the list shows ייצור, כניסה,
 * then the rest. That matches "I just used it, keep it close".
 */

function storageKey(siteId, userId) {
  return `huri.pinned-gates.${userId || 'guest'}.${siteId || 'unknown'}`;
}

function readInitial(siteId, userId) {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(siteId, userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStorage(siteId, userId, ids) {
  try {
    window.localStorage.setItem(storageKey(siteId, userId), JSON.stringify(ids));
  } catch {
    /* localStorage full or blocked — pin still works for the session. */
  }
}

export default function usePinnedGates(siteId, userId) {
  const [pinnedIds, setPinnedIds] = useState(() => readInitial(siteId, userId));

  const isPinned = useCallback(
    (gateId) => pinnedIds.includes(gateId),
    [pinnedIds],
  );

  const togglePin = useCallback((gateId) => {
    setPinnedIds(prev => {
      const next = prev.includes(gateId)
        ? prev.filter(id => id !== gateId)
        : [gateId, ...prev];  // new pin goes to the top
      writeStorage(siteId, userId, next);
      return next;
    });
  }, [siteId, userId]);

  // Sort a gates array so pinned gates lead, in pin-order. Stable for
  // the unpinned tail — preserves whatever ordering the caller passed.
  const sortPinnedFirst = useCallback((gates) => {
    if (!pinnedIds.length) return gates;
    const pinnedSet = new Set(pinnedIds);
    const indexOf = new Map(pinnedIds.map((id, i) => [id, i]));
    const pinned = gates
      .filter(g => pinnedSet.has(g.id))
      .sort((a, b) => indexOf.get(a.id) - indexOf.get(b.id));
    const rest = gates.filter(g => !pinnedSet.has(g.id));
    return [...pinned, ...rest];
  }, [pinnedIds]);

  return { isPinned, togglePin, sortPinnedFirst };
}
